/**
 * Validation utilities
 */

/**
 * Validate and format phone number
 * Only allows digits, removes all non-numeric characters
 * @param {string} value - Input value
 * @returns {string} - Cleaned phone number with only digits
 */
export const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  return value.replace(/\D/g, '');
};

/**
 * Validate phone number format
 * Indonesian phone numbers: 8-15 digits, optionally starts with 62 or 0
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid
 */
export const isValidPhoneNumber = (phone) => {
  if (!phone) return true; // Optional field
  
  const cleanPhone = formatPhoneNumber(phone);
  
  // Must be 8-15 digits
  if (cleanPhone.length < 8 || cleanPhone.length > 15) {
    return false;
  }
  
  // Must start with 0, 62, or 8
  if (!cleanPhone.match(/^(0|62|8)/)) {
    return false;
  }
  
  return true;
};

/**
 * Get phone validation error message
 * @param {string} phone - Phone number to validate
 * @returns {string|null} - Error message or null if valid
 */
export const getPhoneValidationError = (phone) => {
  if (!phone) return null;
  
  const cleanPhone = formatPhoneNumber(phone);
  
  if (cleanPhone.length === 0) {
    return null;
  }
  
  if (cleanPhone.length < 8) {
    return 'Nomor telepon minimal 8 digit';
  }
  
  if (cleanPhone.length > 15) {
    return 'Nomor telepon maksimal 15 digit';
  }
  
  if (!cleanPhone.match(/^(0|62|8)/)) {
    return 'Nomor harus diawali 0, 62, atau 8';
  }
  
  return null;
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @returns {string} - Formatted phone number
 */
export const displayPhoneNumber = (phone) => {
  if (!phone) return '';
  const clean = formatPhoneNumber(phone);
  
  // Format: 0812-3456-7890
  if (clean.startsWith('0') && clean.length >= 10) {
    return clean.replace(/(\d{4})(\d{4})(\d+)/, '$1-$2-$3');
  }
  
  return clean;
};


/**
 * Safely trim a value that might be null/undefined/number/string.
 * Returns an empty string if value is falsy or not a string-like.
 * @param {*} v - Any value
 * @returns {string} - Trimmed string or ''
 */
export const safeTrim = (v) => {
  if (v === null || v === undefined) return '';
  return String(v).trim();
};
